export default function Home() {
  return (
    <main className="min-h-screen bg-background-50">
      {/* Hero Section */}
      <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20flowing%20geometric%20composition%20with%20overlapping%20organic%20curves%20and%20soft%20gradients%2C%20warm%20terracotta%20and%20olive%20green%20tones%20with%20cream%20highlights%2C%20minimalist%20modern%20aesthetic%2C%20subtle%20layered%20texture%2C%20elegant%20smooth%20forms%2C%20editorial%20design%20background%2C%20no%20text%20no%20people%2C%20sophisticated%20abstract%20art%2C%20high-end%20professional%20atmosphere%2C%20soft%20diffused%20lighting%2C%20serene%20and%20refined%20mood&width=1600&height=900&seq=maro-hero-bg&orientation=landscape"
          alt="Abstract geometric background"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/40"></div>
        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-light tracking-wide text-white">
            Maro Soft d. o. o.
          </h1>
          <p className="mt-6 md:mt-8 text-base md:text-lg text-white/70 font-body font-light max-w-2xl mx-auto leading-relaxed">
            Professional engineering for the iOS platform and related technologies
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="w-full py-20 md:py-28 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:gap-16 gap-8">
            <div className="lg:w-1/3">
              <p className="text-xs md:text-sm font-body font-medium tracking-widest uppercase text-accent-600">
                About
              </p>
            </div>
            <div className="lg:w-2/3 space-y-5">
              <p className="text-base md:text-lg text-foreground-700 font-body leading-relaxed">
                Maro Soft d. o. o. is a professional engineering team focused on designing and developing modern digital software solutions for the iOS platform and related technologies. The company builds scalable, maintainable, and high-performance systems with a strong focus on usability, stability, and long-term technical quality.
              </p>
              <p className="text-base md:text-lg text-foreground-600 font-body leading-relaxed">
                Based in Split, Croatia, the team follows modern software engineering practices, including clean architecture, performance optimization, and user-centered design. Every project is approached with a commitment to technical excellence and reliable, future-ready outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <hr className="border-background-200/70" />
      </div>

      {/* Core Capabilities Section */}
      <section className="w-full py-20 md:py-28 px-4 md:px-6 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:gap-16 gap-8">
            <div className="lg:w-1/3">
              <p className="text-xs md:text-sm font-body font-medium tracking-widest uppercase text-accent-600">
                Capabilities
              </p>
            </div>
            <div className="lg:w-2/3 space-y-10">
              <div>
                <h3 className="text-lg md:text-xl font-heading font-medium text-foreground-900">
                  iOS Platform Engineering
                </h3>
                <p className="mt-3 text-base text-foreground-600 font-body leading-relaxed">
                  Deep expertise in the iOS ecosystem, building applications that leverage the full capabilities of the platform. The team delivers solutions that are performant, intuitive, and maintainable over the long term.
                </p>
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-heading font-medium text-foreground-900">
                  Scalable System Architecture
                </h3>
                <p className="mt-3 text-base text-foreground-600 font-body leading-relaxed">
                  Systems designed to grow — from initial architecture through to production deployment. The team prioritizes modularity, testability, and clean separation of concerns to ensure codebases remain manageable as they evolve.
                </p>
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-heading font-medium text-foreground-900">
                  User-Centered Design
                </h3>
                <p className="mt-3 text-base text-foreground-600 font-body leading-relaxed">
                  Technical decisions are guided by the end-user experience. Every interface is crafted with attention to usability, accessibility, and visual clarity, ensuring that complex systems feel simple and intuitive.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <hr className="border-background-200/70" />
      </div>

      {/* Engineering Approach Section */}
      <section className="w-full py-20 md:py-28 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:gap-16 gap-8">
            <div className="lg:w-1/3">
              <p className="text-xs md:text-sm font-body font-medium tracking-widest uppercase text-accent-600">
                Approach
              </p>
            </div>
            <div className="lg:w-2/3 space-y-10">
              <div>
                <h3 className="text-lg md:text-xl font-heading font-medium text-foreground-900">
                  Clean Architecture
                </h3>
                <p className="mt-3 text-base text-foreground-600 font-body leading-relaxed">
                  Every project is structured around clear architectural boundaries. The team applies well-established patterns to keep business logic decoupled from presentation and infrastructure layers, enabling easier testing, faster iteration, and smoother long-term maintenance.
                </p>
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-heading font-medium text-foreground-900">
                  Performance Optimization
                </h3>
                <p className="mt-3 text-base text-foreground-600 font-body leading-relaxed">
                  Performance is treated as a first-class concern throughout the development lifecycle. From rendering efficiency to memory management and network optimization, every layer of the stack is tuned to deliver a responsive and fluid experience.
                </p>
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-heading font-medium text-foreground-900">
                  Continuous Improvement
                </h3>
                <p className="mt-3 text-base text-foreground-600 font-body leading-relaxed">
                  Development workflows are regularly evaluated and refined. The team actively explores new technologies and adopts up-to-date engineering standards to keep its processes efficient and its output at the highest level of technical quality.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <hr className="border-background-200/70" />
      </div>

      {/* Values Section */}
      <section className="w-full py-20 md:py-28 px-4 md:px-6 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:gap-16 gap-8">
            <div className="lg:w-1/3">
              <p className="text-xs md:text-sm font-body font-medium tracking-widest uppercase text-accent-600">
                Values
              </p>
            </div>
            <div className="lg:w-2/3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10">
                <div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/70 mb-4">
                    <i className="ri-shield-check-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-base md:text-lg font-heading font-medium text-foreground-900">
                    Reliability
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-foreground-600 font-body leading-relaxed">
                    Systems built to perform consistently under real-world conditions, with thorough testing and monitoring at every stage.
                  </p>
                </div>
                <div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100/70 mb-4">
                    <i className="ri-stack-line text-xl text-secondary-600"></i>
                  </div>
                  <h3 className="text-base md:text-lg font-heading font-medium text-foreground-900">
                    Maintainability
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-foreground-600 font-body leading-relaxed">
                    Codebases structured for clarity, documented with intent, and designed to evolve without accumulating technical debt.
                  </p>
                </div>
                <div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100/70 mb-4">
                    <i className="ri-lightbulb-line text-xl text-primary-600"></i>
                  </div>
                  <h3 className="text-base md:text-lg font-heading font-medium text-foreground-900">
                    Innovation
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-foreground-600 font-body leading-relaxed">
                    A culture of continuous learning and experimentation, keeping the team at the forefront of modern engineering practices.
                  </p>
                </div>
                <div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/70 mb-4">
                    <i className="ri-focus-3-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-base md:text-lg font-heading font-medium text-foreground-900">
                    Precision
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-foreground-600 font-body leading-relaxed">
                    Attention to detail at every level — from architecture decisions to the smallest interface element — ensures consistently high-quality output.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="w-full py-20 md:py-28 px-4 md:px-6 bg-secondary-900">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:gap-16 gap-8">
            <div className="lg:w-1/3">
              <p className="text-xs md:text-sm font-body font-medium tracking-widest uppercase text-secondary-200">
                Contact
              </p>
            </div>
            <div className="lg:w-2/3 space-y-6">
              <div>
                <p className="text-sm font-body font-medium text-secondary-200 uppercase tracking-wider">
                  Address
                </p>
                <p className="mt-2 text-base md:text-lg text-background-50 font-body leading-relaxed">
                  Ulica Oca Ante Gabrica 32
                  <br />
                  21000 Split
                  <br />
                  Croatia
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-body font-medium text-secondary-200 uppercase tracking-wider">
                    Phone
                  </p>
                  <p className="mt-2 text-base text-background-50 font-body">
                    +385 0957965483
                  </p>
                </div>
                <div>
                  <p className="text-sm font-body font-medium text-secondary-200 uppercase tracking-wider">
                    Email
                  </p>
                  <p className="mt-2 text-base text-background-50 font-body">
                    contact@maro-soft.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full py-8 px-4 md:px-6 bg-secondary-950">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs md:text-sm text-secondary-300 font-body text-center">
            &copy; {new Date().getFullYear()} Maro Soft d. o. o. All rights reserved.
          </p>
        </div>
      </footer>
    </main>
  );
}